import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme, useMediaQuery, Box, Slide } from '@mui/material';
import { Group, Panel, Separator } from 'react-resizable-panels';
import ConversationSidebar from './components/ConversationSidebar';
import Chat from './components/Chat/Chat';
import DocumentViewer from '../../components/document-viewer/DocumentViewer';
import ArtifactPreview from './components/ArtifactPreview';
import { fetchConversation, createConversation } from '../../redux/actions/conversationAction';
import { setMobileHistoryOpen } from '../../redux/actions/chatUIAction';
import { setDocument, clearDocument } from '../../redux/actions/documentAction';

/**
 * ConversationLayout - Main 3-panel layout component
 * Manages the layout of sidebar, chat, and document viewer panels
 * with responsive behavior for mobile/tablet/desktop
 */
const ConversationLayout = () => {
  const { conversationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const creatingRef = useRef(false);

  // Media queries
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px

  // Redux state
  useSelector(state => state.conversations || { conversation: null, messages: [], loading: false });
  const { document: documentData, documentSourceUrl, selectedCitation, isLoadingDocument } = useSelector(state => state.document || { document: null, documentSourceUrl: null, selectedCitation: null, isLoadingDocument: false });
  const { sidebarCollapsed, mobileHistoryOpen } = useSelector(state => state.chatUI || { sidebarCollapsed: false, mobileHistoryOpen: false });
  const userId = useSelector(state => state.auth?.user?.user?.id);

  // Local state
  const [documentVisible, setDocumentVisible] = useState(false);
  const [panelWidths, setPanelWidths] = useState({ chat: 50, document: 50 });
  const [panelAnimating, setPanelAnimating] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Mutual exclusion: preview open → close document viewer
  const handlePreviewChange = useCallback((content, title, type) => {
    if (content) {
      dispatch(clearDocument());
    }
    setPreviewContent(content);
    setPreviewTitle(title || null);
    setPreviewType(type || null);
  }, [dispatch]);

  // Mutual exclusion: document viewer open → close preview
  useEffect(() => {
    if ((documentData || documentSourceUrl) && previewContent) {
      setPreviewContent(null);
    }
  }, [documentData, documentSourceUrl]);

  // Auto-create conversation when landing on /conversation with no ID
  useEffect(() => {
    if (!conversationId && !creatingRef.current) {
      creatingRef.current = true;
      dispatch(createConversation(userId)).then((newConversation) => {
        if (newConversation?.id) {
          navigate(`/conversation/${newConversation.id}`, { replace: true });
        }
        creatingRef.current = false;
      });
    }
  }, [conversationId, dispatch, navigate, userId]);

  // Fetch conversation when ID changes — also close any open preview panels
  useEffect(() => {
    if (conversationId) {
      dispatch(fetchConversation(conversationId));
      dispatch(clearDocument());
      setPreviewContent(null);
      setPreviewTitle(null);
    }
  }, [conversationId, dispatch]);

  // Handle document visibility with animation
  useEffect(() => {
    if (documentData || documentSourceUrl) {
      if (!documentVisible) {
        setPanelAnimating(true);
        // Delay panel expansion for smooth animation
        setTimeout(() => {
          setDocumentVisible(true);
          setPanelAnimating(false);
        }, prefersReducedMotion ? 0 : 50);
      }
    } else if (documentVisible) {
      setPanelAnimating(true);
      setTimeout(() => {
        setDocumentVisible(false);
        setPanelAnimating(false);
      }, prefersReducedMotion ? 0 : 300);
    }
  }, [documentData, documentSourceUrl, documentVisible, prefersReducedMotion]);

  // Close document on mobile when resizing
  useEffect(() => {
    if (isMobile && documentVisible) {
      setDocumentVisible(false);
    }
  }, [isMobile]);

  // Sidebar width calculations
  const sidebarWidth = sidebarCollapsed ? 70 : 280;

   // Render mobile layout - same Navbar header as other pages, drawer controlled via Redux
   // Use calc(100vh - 64px) to account for the fixed Navbar AppBar height
   if (isMobile) {
     return (
       <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
         <ConversationSidebar
           mobileDrawerOpen={mobileHistoryOpen}
           onMobileDrawerClose={() => dispatch(setMobileHistoryOpen(false))}
         />
         <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
           <Chat onPreviewChange={handlePreviewChange} previewContent={previewContent} />
         </Box>
       </Box>
     );
   }
 
   // Render tablet layout - collapsed sidebar + chat + optional document modal
   if (isTablet && !isDesktop) {
     return (
       <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
         <ConversationSidebar />
         <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
           <Chat onPreviewChange={handlePreviewChange} previewContent={previewContent} />
         </Box>
       </Box>
     );
   }

   // Render desktop layout - sidebar + resizable chat/document panels
   // Always render the same component tree to prevent Chat from unmounting
   return (
     <Box
       sx={{
         display: 'flex',
         height: '100%',
         overflow: 'hidden',
         backgroundColor: theme.palette.background.default,
       }}
     >
       {/* Left Sidebar */}
       <ConversationSidebar />

       {/* Main Content Area - Chat + right panel (Document OR Artifact Preview) */}
       <Box sx={{ flex: 1, display: 'flex', height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
         <Group direction="horizontal" style={{ width: '100%', height: '100%' }}>
           {/* Chat Panel - always rendered */}
           <Panel defaultSize={(documentVisible && documentData) || previewContent ? panelWidths.chat : 100} minSize={30}>
             <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, overflow: 'hidden' }}>
               <Chat onPreviewChange={handlePreviewChange} previewContent={previewContent} />
             </Box>
           </Panel>

           {/* Resize Handle - visible when either panel is open */}
           {((documentVisible && documentData) || previewContent) && (
             <Separator
               style={{
                 width: '4px',
                 backgroundColor: theme.palette.divider,
                 cursor: 'col-resize',
                 transition: prefersReducedMotion
                   ? 'none'
                   : 'background-color 0.2s ease, opacity 0.2s ease',
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.backgroundColor = theme.palette.primary.main;
                 e.currentTarget.style.opacity = '0.7';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.backgroundColor = theme.palette.divider;
                 e.currentTarget.style.opacity = '1';
               }}
             />
           )}

           {/* Document Panel - only when document is open (and no preview) */}
           {documentVisible && documentData && !previewContent && (
             <Panel defaultSize={panelWidths.document} minSize={25}>
               <Slide
                 direction="left"
                 in={!panelAnimating}
                 timeout={prefersReducedMotion ? 0 : { enter: 400, exit: 300 }}
                 easing="cubic-bezier(0.4, 0, 0.2, 1)"
               >
                 <Box
                   data-tour="chat-doc-viewer"
                   sx={{
                     height: '100%',
                     minWidth: 0,
                     overflow: 'auto',
                     backgroundColor: theme.palette.background.paper,
                     willChange: panelAnimating ? 'transform, opacity' : 'auto'
                   }}
                 >
                   <DocumentViewer document={documentData} documentSourceUrl={documentSourceUrl} selectedCitation={selectedCitation} isLoadingDocument={isLoadingDocument} setDocument={setDocument} />
                 </Box>
               </Slide>
             </Panel>
           )}

           {/* Artifact Preview Panel - only when preview is open (and no document) */}
           {previewContent && !(documentVisible && documentData) && (
             <Panel defaultSize={panelWidths.document} minSize={25}>
               <Box sx={{ height: '100%', minWidth: 0, overflow: 'hidden' }}>
                 <ArtifactPreview
                   content={previewContent}
                   title={previewTitle}
                   artifactType={previewType}
                   onClose={() => { setPreviewContent(null); setPreviewTitle(null); setPreviewType(null); }}
                 />
               </Box>
             </Panel>
           )}
         </Group>
       </Box>
     </Box>
   );
};

export default ConversationLayout;
